import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { 
  CheckCircleIcon, 
  DocumentArrowDownIcon,
  VideoCameraIcon,
  SparklesIcon 
} from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/ui/Button'
import { useAppSelector } from '@/hooks/redux'
import { useTheme } from '@/contexts/ThemeContext'
import { apiUrl } from '@/utils/api'

interface AssessmentSummary {
  assessed_competency_count: number
  total_competency_count: number
}

const CompletionPage: React.FC = () => {
  const navigate = useNavigate()
  const { candidateName, position } = useAppSelector(state => state.auth)
  const [timeElapsed, setTimeElapsed] = useState(0)
  const [assessmentSummary, setAssessmentSummary] = useState<AssessmentSummary | null>(null)
  const { fontSize } = useTheme()

  useEffect(() => {
    // Calculate interview duration from start time if available
    const startTime = localStorage.getItem('interview-start-time')
    if (startTime) {
      const elapsed = Math.floor((Date.now() - parseInt(startTime)) / 1000)
      setTimeElapsed(elapsed)
    }

    const sessionId = localStorage.getItem('interview-session-id')
    if (!sessionId) {
      setAssessmentSummary(null)
      return
    }

    const fetchAssessmentSummary = async () => {
      try {
        const response = await fetch(
          apiUrl(`/api/v1/interview/sessions/${sessionId}/assessment-summary`)
        )
        if (!response.ok) {
          console.error('Failed to fetch assessment summary:', response.status, response.statusText)
          return
        }
        const data: AssessmentSummary = await response.json()
        setAssessmentSummary(data)
      } catch (error) {
        console.error('Error fetching assessment summary:', error)
      }
    }

    fetchAssessmentSummary()

    // Don't automatically clear session data - keep it until user explicitly leaves
    // This ensures the auth guard always has the session data it needs
  }, [])

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const fontClass =
    fontSize === 'small'
      ? 'font-small'
      : fontSize === 'large'
      ? 'font-large'
      : 'font-medium'

  return (
    <div className={`min-h-screen bg-gradient-to-br from-snwolley-green-50 via-white to-snwolley-yellow-50 flex items-center justify-center p-4 ${fontClass}`}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-2xl w-full"
      >
        <div className="card p-8 text-center">
          {/* Success Animation */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mb-6"
          >
            <div className="w-24 h-24 bg-gradient-snwolley rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-12 h-12 text-white" />
            </div>
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Interview Completed! 🎉
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              Thank you {candidateName} for completing your interview for the {position} position
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
          >
            <div className="bg-gray-50 rounded-lg p-4">
              <SparklesIcon className="w-8 h-8 text-snwolley-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">
                {assessmentSummary === null
                  ? '—'
                  : `${assessmentSummary.assessed_competency_count} / ${assessmentSummary.total_competency_count}`}
              </div>
              <div className="text-sm text-gray-600">Competencies Assessed</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <VideoCameraIcon className="w-8 h-8 text-snwolley-yellow-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">Recorded</div>
              <div className="text-sm text-gray-600">Video Interview</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <DocumentArrowDownIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{formatDuration(timeElapsed)}</div>
              <div className="text-sm text-gray-600">Total Duration</div>
            </div>
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-snwolley-green-50 rounded-lg p-6 mb-6"
          >
            <h3 className="text-lg font-semibold text-snwolley-green-800 mb-2">
              What happens next?
            </h3>
            <ul className="text-left text-snwolley-green-700 space-y-2">
              <li className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-snwolley-green-600 mr-2 mt-0.5 flex-shrink-0" />
                Your video interview is being processed and analyzed
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="w-5 h-5 text-snwolley-green-600 mr-2 mt-0.5 flex-shrink-0" />
                The hiring team will review your assessment and get in touch
              </li>
            </ul>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="space-y-4"
          >
            <Button
              onClick={() => {
                // Clear any remaining interview data (session data already cleared automatically)
                localStorage.removeItem('interview-state')
                localStorage.removeItem('snwolley-media-permissions')
                localStorage.removeItem('interviewInputType')
                // Session data was already cleared automatically, but clean up just in case
                localStorage.removeItem('interview-session-id')
                localStorage.removeItem('interview-start-time')
                navigate('/')
              }}
              variant="primary"
              className="bg-gradient-snwolley hover:shadow-snwolley-md w-full"
              size="lg"
            >
              Return to Home
            </Button>
            
            <p className="text-xs text-gray-500">
              You can safely close this window. We'll be in touch soon!
            </p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}

export default CompletionPage